import { useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "sonner";
import communityService from "../../../../api/communityService";

const PollWidget = ({ poll, readOnly = false }) => {
  const isMulti = Boolean(poll.is_multi_select);
  const initialVotedIds = poll.user_voted_option_ids || [];

  const [localPoll, setLocalPoll] = useState(poll);
  // Single-select: one ID or null. Multi-select: array of IDs.
  const [selectedIds, setSelectedIds] = useState(
    isMulti ? initialVotedIds : initialVotedIds.length > 0 ? initialVotedIds[0] : null
  );
  const [voted, setVoted] = useState(initialVotedIds.length > 0);
  const [submitting, setSubmitting] = useState(false);

  const totalVotes = localPoll.total_votes || 0;
  const currentTotal = voted ? localPoll.total_votes : totalVotes;

  const isSelected = (optId) =>
    isMulti ? Array.isArray(selectedIds) && selectedIds.includes(optId) : selectedIds === optId;

  const toggleOption = (optId) => {
    if (voted || readOnly) return;
    if (isMulti) {
      setSelectedIds((prev) => {
        const arr = Array.isArray(prev) ? prev : [];
        return arr.includes(optId) ? arr.filter((id) => id !== optId) : [...arr, optId];
      });
    } else {
      setSelectedIds(optId);
    }
  };

  const canVote = isMulti
    ? Array.isArray(selectedIds) && selectedIds.length > 0
    : selectedIds !== null;

  const handleVote = async () => {
    if (!canVote || submitting || voted) return;
    setSubmitting(true);
    try {
      await communityService.submitPollVote(localPoll.id, isMulti ? selectedIds : selectedIds);
      const votedIds = isMulti ? selectedIds : [selectedIds];
      setLocalPoll((prev) => ({
        ...prev,
        options: prev.options.map((opt) =>
          votedIds.includes(opt.id)
            ? { ...opt, vote_count: (opt.vote_count || 0) + 1 }
            : opt
        ),
        total_votes: (prev.total_votes || 0) + (isMulti ? 1 : 1),
        user_voted_option_ids: votedIds,
      }));
      setVoted(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to submit vote. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="post-poll-widget"
      style={{
        margin: "12px 0",
        padding: "14px 16px",
        background: "var(--mmp-accent-bg)",
        borderRadius: "10px",
        border: "1px solid var(--mmp-accent-border)",
      }}
    >
      <p
        style={{
          fontWeight: 600,
          fontSize: "14px",
          marginBottom: "6px",
          color: "var(--mmp-text-primary)",
        }}
      >
        {localPoll.question}
      </p>

      {isMulti && !voted && (
        <p style={{ fontSize: "11px", color: "var(--mmp-accent)", marginBottom: "8px", fontWeight: 500 }}>
          Select one or more options
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {localPoll.options.map((option) => {
          const pct =
            currentTotal > 0
              ? Math.round((option.vote_count / currentTotal) * 100)
              : 0;
          const isUserChoice =
            isSelected(option.id) ||
            (localPoll.user_voted_option_ids || []).includes(option.id);

          return (
            <div
              key={option.id}
              onClick={() => toggleOption(option.id)}
              style={{
                position: "relative",
                borderRadius: "8px",
                border: `2px solid ${isUserChoice && voted ? "var(--mmp-accent)" : voted ? "var(--mmp-border)" : isSelected(option.id) ? "var(--mmp-accent)" : "var(--mmp-accent-border)"}`,
                overflow: "hidden",
                cursor: voted || readOnly ? "default" : "pointer",
                background: "var(--mmp-surface)",
                minHeight: "38px",
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
              }}
            >
              {/* Progress bar fill (shown after voting) */}
              {voted && (
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: `${pct}%`,
                    background: isUserChoice ? "var(--mmp-accent-bg)" : "var(--mmp-hover-bg)",
                    transition: "width 0.5s ease",
                    zIndex: 0,
                  }}
                />
              )}

              {/* Input (radio or checkbox, pre-vote only, hidden when readOnly) */}
              {!voted && !readOnly && (
                <input
                  type={isMulti ? "checkbox" : "radio"}
                  name={`poll-${localPoll.id}`}
                  checked={isSelected(option.id)}
                  onChange={() => toggleOption(option.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginRight: "8px", zIndex: 1, accentColor: "var(--mmp-accent)" }}
                />
              )}

              {/* Option text */}
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  fontSize: "13px",
                  fontWeight: isUserChoice && voted ? 600 : 400,
                  color: "var(--mmp-text-primary)",
                  flex: 1,
                }}
              >
                {option.option_text}
              </span>

              {/* Percentage (shown after voting) */}
              {voted && (
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: isUserChoice ? "var(--mmp-accent)" : "var(--mmp-text-secondary)",
                    marginLeft: "8px",
                  }}
                >
                  {pct}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: "12px", color: "var(--mmp-text-secondary)" }}>
          {currentTotal} {currentTotal === 1 ? "vote" : "votes"}
        </span>

        {!voted && !readOnly && (
          <button
            type="button"
            onClick={handleVote}
            disabled={!canVote || submitting}
            style={{
              background: canVote ? "var(--mmp-accent)" : "var(--mmp-border)",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "6px 18px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: canVote ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {submitting && (
              <Spinner animation="border" size="sm" style={{ width: "12px", height: "12px" }} />
            )}
            Vote
          </button>
        )}
      </div>
    </div>
  );
};

export default PollWidget;
